import { AceAusschreibungAceAusschreibungEnumsAceAusschreibungStatuscode } from './../../../../../api/models/ace/xFound/Portal/Core/Entities/ace-ausschreibung-ace-ausschreibung-enums-ace-ausschreibung-statuscode';
import { Component, ErrorHandler, inject, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { AnfrageService, AusschreibungService } from '@/api/service-index';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { GlobalEnumerationsAceXfoundportalobjekt } from '@/api/models/ace/xFound/Portal/Core/Entities/global-enumerations-ace-xfoundportalobjekt';
import { PortalContextService } from '@/core/services/portal-context.service';
import { AbschnittDisplayModel, AnfrageDisplayModel, AusschreibungDisplayModel, GlobalEnumerationsAceBenutzerrolle } from '@/api/model-index';
import { I18nService } from '@/core/services/i18nService';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { DocumentSelectionDialogComponent, UnterlagenDialogResult } from '../../../../components/document/document-selection-dialog/document-selection-dialog.component';
import { DocumentService } from '@/core/services/document.service';
import { Constants } from '@/constants';
import { AusschreibungDialogResult, AusschreibungErstellenDialogComponent } from '@/core/components/dialogs/ausschreibung-erstellen-dialog/ausschreibung-erstellen-dialog.component';
import { FormularService } from '@/core/services/formular.service';
import { ComponentService } from '@/core/services/component.service';
import { ExtendedAuthenticationService } from '@/core/components/authentication/services/extended-authentication.service';
import { StammdatenService } from '../../../../services/stammdaten.service';

@Component({
  selector: 'ausschreibung-card',
  templateUrl: './ausschreibung-card.component.html',
  styleUrls: ['./ausschreibung-card.component.scss'],
  standalone: false
})
export class AusschreibungCardComponent {
  public authenticationService = inject(ExtendedAuthenticationService);
  public config = Constants.AUSSCHREIBUNG_OVERVIEW;

  @Input() public ausschreibung: AusschreibungDisplayModel;
  @Input() public foerderprogrammName?: string;

  protected stammdatenService = inject(StammdatenService);

  protected unterlagenDialog: DialogRef;
  protected erstellenDialog: DialogRef

  public get isProjektbeschreibungVisible(): boolean {
    return this.ausschreibung != null && this.ausschreibung.goalDescription != null && this.getGoalDescriptionText(this.ausschreibung).length > 0;
  }

  public get isNoDescriptionMessageVisible(): boolean {
    return !this.isProjektbeschreibungVisible;
  }

  public get isInPreview(): boolean {
    return this.ausschreibung.ausschreibungsStatus == AceAusschreibungAceAusschreibungEnumsAceAusschreibungStatuscode.InPlanung && this.authenticationService.HasAdminrolle;
  }

  public get isOpen(): boolean {
    return this.ausschreibung.ausschreibungsStatus == AceAusschreibungAceAusschreibungEnumsAceAusschreibungStatuscode.InDurchfuhrung &&
      (moment(Date.now()).isBetween(moment(this.ausschreibung.dateFrom), moment(this.ausschreibung.dateTo), "minute", "[]"));
  }

  public get isPermanentOpen(): boolean {
    return this.ausschreibung.ausschreibungsStatus == AceAusschreibungAceAusschreibungEnumsAceAusschreibungStatuscode.InDurchfuhrung &&
      (moment(Date.now()).isSameOrAfter(moment(this.ausschreibung.dateFrom), "minute")) &&
      this.ausschreibung.dateTo == null;
  }

  public get isExpired(): boolean {
    return !this.isOpen;
  }

  public get isNotYetOpen(): boolean {
    return !this.isOpen && !this.isPermanentOpen && (moment(Date.now()).isBefore(moment(this.ausschreibung.dateFrom), "minute"));
  }

  public get canApply(): boolean {
    return !this.isExpired || this.isPermanentOpen || this.isInPreview;
  }

  public get ausschreibungsName(): string {
    const language = this.ausschreibung.sprache ? this.languageService.GetLanguageFromEnum(this.ausschreibung.sprache) : this.languageService.language;

    return this.languageService.getLocalizedTextDisplayModelForSpecificLanguage(this.ausschreibung.name, language)?.text ?? this.languageService.instant('undefinedText');;
  }

  constructor(protected router: Router,
    protected route: ActivatedRoute,
    protected anfrageService: AnfrageService,
    protected loadingAnimationService: LoadingAnimationService,
    protected errorHandler: ErrorHandler,
    protected portalContextService: PortalContextService,
    public languageService: I18nService,
    public ausschreibungService: AusschreibungService,
    public dialogService: DialogService,
    public documentService: DocumentService,
    protected formularService: FormularService) {
  }

  public async onCreateClick() {
    var existingAnfrageList = await this.checkIfExistingAnfrage();

    if (existingAnfrageList && existingAnfrageList?.length > 0) {
      //to only unhide spinner if dialog opens - to prevent flickering between promise completion and createAnfrage start
      this.loadingAnimationService.showSpinnerUsingPromise(new Promise(resolve => resolve(0)));
      this.openExistingAnfrageDialog(existingAnfrageList);
    }
    else {
      this.createAnfrage(this.ausschreibung.phasen![0].portalObject);
    }
  }

  public getGoalDescriptionText(ausschreibung: AusschreibungDisplayModel): string {
    const language = this.ausschreibung.sprache ? this.languageService.GetLanguageFromEnum(this.ausschreibung.sprache) : this.languageService.language;

    return this.languageService.getLocalizedTextDisplayModelForSpecificLanguage(ausschreibung.goalDescription, language)?.text ?? "";
  }

  public createAnfrage(itemScope: GlobalEnumerationsAceXfoundportalobjekt | undefined) {
    if (this.ausschreibung.id) {
      let scope: GlobalEnumerationsAceXfoundportalobjekt = itemScope ?? this.ausschreibung.portalObject as unknown as GlobalEnumerationsAceXfoundportalobjekt;

      let subscription = this.anfrageService.apiAnfrageScopePost({ scope: scope, body: JSON.stringify(this.ausschreibung.id) })
        .subscribe(anfrage => {
          this.portalContextService.Anfragen = null;
          this.portalContextService.tempAnfrageId = anfrage.id;

          if (anfrage.phase && anfrage.phase.abschnitte) {
            // Group abschnitte into parent-child hierarchy
            anfrage.phase.abschnitte = this.groupAbschnitteByParent(anfrage.phase.abschnitte);
          }

          this.portalContextService.CurrentAnfrage = anfrage;
          this.router.navigateByUrl('anfrage/detail/' + scope + '/' + anfrage.id);
        }, (error) => {
          this.errorHandler.handleError(error);
          this.loadingAnimationService.hide();
        });

      this.loadingAnimationService.donNotHide().showSpinnerUsingSubscription(subscription);
    }
  }

  protected groupAbschnitteByParent(abschnitte: AbschnittDisplayModel[]): AbschnittDisplayModel[] {
    // Separate parent abschnitte (those without parentId) and child abschnitte
    const parentAbschnitte = abschnitte.filter(a => !a.parentId);
    const childAbschnitte = abschnitte.filter(a => a.parentId);

    // Group children by their parentId
    const childrenByParentId = new Map<string, typeof abschnitte>();
    childAbschnitte.forEach(child => {
      if (!child.parentId) return;

      if (!childrenByParentId.has(child.parentId)) {
        childrenByParentId.set(child.parentId, []);
      }
      childrenByParentId.get(child.parentId)!.push(child);
    });

    // Sort children by reihenfolge for each parent
    childrenByParentId.forEach((children, parentId) => {
      children.sort((a, b) => (a.reihenfolge ?? 0) - (b.reihenfolge ?? 0));
    });

    // Assign sorted children to their parent abschnitte
    parentAbschnitte.forEach(parent => {
      if (parent.id && childrenByParentId.has(parent.id)) {
        parent.abschnitte = childrenByParentId.get(parent.id);
      }
    });

    // Sort parent abschnitte by reihenfolge
    parentAbschnitte.sort((a, b) => (a.reihenfolge ?? 0) - (b.reihenfolge ?? 0));

    // Update the phase with the sorted and grouped parent abschnitte only
    return parentAbschnitte;
  }

  public isLogoVisible(ausschreibung: AusschreibungDisplayModel): boolean {
    return ausschreibung.logo != null;
  }

  public get getApplyButtonText(): string {
    if (this.isInPreview) {
      return this.languageService.instant("tender.preview");
    }
    else {
      let overridetext = this.languageService.getCurrentLocalizedTextDisplayModel(this.stammdatenService.Portalkonfiguration.ausschreibungskachelButtonTexte ?? [])
      if (overridetext && overridetext.text && overridetext.text.length > 0) {
        return overridetext.text;
      }
      return this.languageService.instant("tender.apply");
    }
  }

  public getApplyButtonAriaText(): string {
    return this.getApplyButtonText + " " + this.ausschreibungsName;
  }

  public getButtonText(): string {
    return this.languageService.instant("tender.apply") + " " + this.ausschreibungsName;
  }

  public getLogo(ausschreibung: AusschreibungDisplayModel): string {
    return 'data:image/jpg;base64,' + ausschreibung.logo;
  }

  protected componentService = inject(ComponentService);

  public openUnterlagenDialog(guid: any) {
    let subscription = this.ausschreibungService.apiAusschreibungAusschreibungIdAllUnterlagenGet({ ausschreibungId: guid })
      .subscribe(response => {
        this.unterlagenDialog = this.dialogService.open({
          content: this.componentService.get(DocumentSelectionDialogComponent),
          maxWidth: Constants.DIALOG.MAX_WIDTH,
          maxHeight: Constants.DIALOG.MAX_HEIGHT,
          title: this.languageService.instant("ausschreibung.unterlagen.dialog-title")
        });

        let dialogComponent: DocumentSelectionDialogComponent = this.unterlagenDialog.content.instance;
        dialogComponent.documents = response;

        this.unterlagenDialog.result.subscribe(result => {
          this.onUnterlagenDialogClose(<UnterlagenDialogResult>result, guid);
        });
      });
    this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
  }

  protected onUnterlagenDialogClose(result: UnterlagenDialogResult, id: string) {
    if (!result.documentsToDownload || result.documentsToDownload.length < 1)
      return;

    let documentIds: string[] = result.documentsToDownload.map(document => document.documentId + "");

    let subscription = this.ausschreibungService.apiAusschreibungAusschreibungIdUnterlagenGet({ ausschreibungId: id, documentIds: documentIds })
      .subscribe(response => {
        response.forEach(file => this.documentService.download(file));
      })
    this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
  }

  protected async checkIfExistingAnfrage(): Promise<AnfrageDisplayModel[] | null> {
    let existingAnfragen = this.anfrageService.apiAnfrageAnfrageExistsAusschreibungIdGet({ ausschreibungId: this.ausschreibung.id! }).toPromise();
    this.loadingAnimationService.donNotHide().showSpinnerUsingPromise(existingAnfragen);
    return existingAnfragen;
  }


  protected openExistingAnfrageDialog(existingAnfrageList: AnfrageDisplayModel[]) {
    this.erstellenDialog = this.dialogService.open({
      content: this.componentService.get(AusschreibungErstellenDialogComponent),
      maxWidth: Constants.DIALOG.MAX_WIDTH,
      maxHeight: Constants.DIALOG.MAX_HEIGHT,
      title: this.languageService.instant("ausschreibung.erstellen.dialog-title")
    });

    let dialogComponent: AusschreibungErstellenDialogComponent = this.erstellenDialog.content.instance;

    dialogComponent.anfragen = existingAnfrageList;

    this.erstellenDialog.result.subscribe(result => {
      var res = (<AusschreibungDialogResult>result)
      if (res.createNew == false) {
        if (res.anfrage) {
          //open eisting anfrage
          this.openApplication(res.anfrage);
        }
        else {
          //open anfrage overview
          this.router.navigate(['../', 'anfrage', 'overview'], { relativeTo: this.route });
        }
      }
      else if (res.createNew == true) {
        //crerate new anfrage
        this.createAnfrage(this.ausschreibung.phasen![0].portalObject);
      }

    });
  }


  /**
   * Open application. COPY FROM ANFRAGECARD
   */
  protected openApplication(anfrage: AnfrageDisplayModel) {
    if (!anfrage.phase || !anfrage.phase.abschnitte)
      throw new Error("Anfrage has no phase or abschnitte.");

    let link = this.formularService.getRouteObjectThrough(anfrage.phase.abschnitte[0]).link.split("/");

    let sectionType = link[0];
    let sectionId = link[1];

    this.router.navigate([
      '../',
      'anfrage',
      'detail',
      anfrage.objektTyp,
      anfrage.id,
      sectionType,
      sectionId
    ], { relativeTo: this.route });
  }

}
